import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// Define supported languages
export const supportedLanguages = ['en', 'fr', 'tr', 'de', 'es', 'it', 'ru', 'ar', 'fa'];
export const rtlLanguages = ['ar', 'fa'];

// Language names in their native script
export const languageNames: Record<string, string> = {
  en: 'English',
  fr: 'Français',
  tr: 'Türkçe',
  de: 'Deutsch',
  es: 'Español',
  it: 'Italiano',
  ru: 'Русский',
  ar: 'العربية',
  fa: 'فارسی'
};

// Helper function to get current locale from URL or cookies
export function getCurrentLocale(): string {
  if (typeof window === 'undefined') return 'en';
  
  // Check URL path
  const pathParts = window.location.pathname.split('/');
  const urlLocale = pathParts[1];
  if (supportedLanguages.includes(urlLocale)) {
    return urlLocale;
  }
  
  // Check cookie
  const cookieLocale = document.cookie
    .split('; ')
    .find(row => row.startsWith('locale='))
    ?.split('=')[1];
    
  if (cookieLocale && supportedLanguages.includes(cookieLocale)) {
    return cookieLocale;
  }
  
  return 'en';
}

// Check if current locale is RTL
export function isRTL(locale?: string): boolean {
  const currentLocale = locale || getCurrentLocale();
  return rtlLanguages.includes(currentLocale);
}

// Dynamic import function for translations
async function loadTranslations(locale: string, namespace: string) {
  try {
    const translations = await import(`../locales/${locale}/${namespace}.json`);
    return translations.default;
  } catch (error) {
    // Fallback to English if translation not found
    try {
      const fallback = await import(`../locales/en/${namespace}.json`);
      return fallback.default;
    } catch {
      return {};
    }
  }
}

// Initialize with empty resources - will be loaded dynamically
const resources: any = {};

// Initialize i18n only on the client side
if (typeof window !== "undefined" && !i18n.isInitialized) {
  const currentLocale = getCurrentLocale();
  
  // Load initial translations
  Promise.all([
    loadTranslations(currentLocale, 'common'),
    loadTranslations(currentLocale, 'home'),
    loadTranslations(currentLocale, 'forms'),
    loadTranslations(currentLocale, 'contact'),
    loadTranslations(currentLocale, 'booking'),
    loadTranslations(currentLocale, 'about'),
    loadTranslations(currentLocale, 'treatments'),
    loadTranslations(currentLocale, 'countries'),
    loadTranslations(currentLocale, 'metadata'),
    loadTranslations(currentLocale, 'all-on-4'),
    loadTranslations(currentLocale, 'dental_implants'),
    loadTranslations(currentLocale, 'dental_veneers'),
    loadTranslations(currentLocale, 'hollywood-smile'),
    loadTranslations(currentLocale, 'root-canal')
  ]).then(([common, home, forms, contact, booking, about, treatments, countries, metadata, allOnFour, dentalImplants, dentalVeneers, hollywoodSmile, rootCanal]) => {
    resources[currentLocale] = {
      common,
      home,
      forms,
      contact,
      booking,
      about,
      treatments,
      countries,
      metadata,
      'all-on-4': allOnFour,
      'dental_implants': dentalImplants,
      'dental_veneers': dentalVeneers,
      'hollywood-smile': hollywoodSmile,
      'root-canal': rootCanal
    };
    
    i18n.use(initReactI18next).init({
      resources,
      lng: currentLocale,
      fallbackLng: "en",
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
      },
      ns: ['common', 'home', 'forms', 'contact', 'booking', 'about', 'countries', 'treatments', 'metadata', 'all-on-4', 'dental_implants', 'dental_veneers', 'hollywood-smile', 'root-canal'],
      defaultNS: 'common',
      showSupportNotice: false,
    });
  });
} else if (typeof window !== "undefined" && i18n.isInitialized) {
  // Update language if already initialized
  const currentLocale = getCurrentLocale();
  if (i18n.language !== currentLocale) {
    // Load new language translations
    Promise.all([
      loadTranslations(currentLocale, 'common'),
      loadTranslations(currentLocale, 'home'),
      loadTranslations(currentLocale, 'forms'),
      loadTranslations(currentLocale, 'contact'),
      loadTranslations(currentLocale, 'booking'),
      loadTranslations(currentLocale, 'about'),
      loadTranslations(currentLocale, 'treatments'),
      loadTranslations(currentLocale, 'countries'),
      loadTranslations(currentLocale, 'metadata'),
      loadTranslations(currentLocale, 'all-on-4'),
      loadTranslations(currentLocale, 'dental_implants'),
      loadTranslations(currentLocale, 'dental_veneers'),
      loadTranslations(currentLocale, 'hollywood-smile'),
      loadTranslations(currentLocale, 'root-canal')
    ]).then(([common, home, forms, contact, booking, about, treatments, countries, metadata, allOnFour, dentalImplants, dentalVeneers, hollywoodSmile, rootCanal]) => {
      i18n.addResourceBundle(currentLocale, 'common', common, true, true);
      i18n.addResourceBundle(currentLocale, 'home', home, true, true);
      i18n.addResourceBundle(currentLocale, 'forms', forms, true, true);
      i18n.addResourceBundle(currentLocale, 'contact', contact, true, true);
      i18n.addResourceBundle(currentLocale, 'booking', booking, true, true);
      i18n.addResourceBundle(currentLocale, 'about', about, true, true);
      i18n.addResourceBundle(currentLocale, 'treatments', treatments, true, true);
      i18n.addResourceBundle(currentLocale, 'countries', countries, true, true);
      i18n.addResourceBundle(currentLocale, 'metadata', metadata, true, true);
      i18n.addResourceBundle(currentLocale, 'all-on-4', allOnFour, true, true);
      i18n.addResourceBundle(currentLocale, 'dental_implants', dentalImplants, true, true);
      i18n.addResourceBundle(currentLocale, 'dental_veneers', dentalVeneers, true, true);
      i18n.addResourceBundle(currentLocale, 'hollywood-smile', hollywoodSmile, true, true);
      i18n.addResourceBundle(currentLocale, 'root-canal', rootCanal, true, true);
      i18n.changeLanguage(currentLocale);
    });
  }
}

export default i18n;